import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCampaignConfigSettings } from '../services/campaign';
import { saveCthfConfigSettings } from '../services/cthfRules';
import type { CthfSettingRow } from '../services/cthfRules';
import { campaignConfigSettingKeys } from './useCampaignConfigSettingMutations';

/**
 * Bespoke query + save mutation for the CTHF Rules tab, mirroring useCampaignBannerRules.
 * The query key is the SHARED campaignConfigSettingKeys.settings(campaignId) key (not a
 * bespoke CTHF-only key) so it dedupes with any other consumer of this campaign's settings
 * and the save mutation's invalidation lands correctly.
 */
export const useCampaignCthfRules = (campaignId?: string) => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: campaignConfigSettingKeys.settings(campaignId ?? 'unresolved'),
        queryFn: () => {
            if (!campaignId) {
                throw new Error('useCampaignCthfRules: campaignId is required to fetch CTHF rules');
            }
            return fetchCampaignConfigSettings(campaignId);
        },
        enabled: Boolean(campaignId),
    });

    const saveMutation = useMutation({
        mutationFn: (rows: CthfSettingRow[]) => {
            if (!campaignId) {
                throw new Error('useCampaignCthfRules: campaignId is required to save CTHF rules');
            }
            return saveCthfConfigSettings(campaignId, rows);
        },
        onSuccess: () => {
            if (!campaignId) return;
            queryClient.invalidateQueries({ queryKey: campaignConfigSettingKeys.settings(campaignId) });
        },
    });

    return { query, saveMutation };
};
